import { motion } from "framer-motion";
import { ArrowRight, Heart, Film, Users, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};
const stagger = { visible: { transition: { staggerChildren: 0.15 } } };

const impactAreas = [
  { icon: Film, title: "Film Production", description: "Funding documentaries that bring overlooked stories to light." },
  { icon: Users, title: "Creator Mentorship", description: "Supporting aspiring filmmakers and storytellers." },
  { icon: Globe, title: "Community Outreach", description: "Connecting with local causes and organizations." },
  { icon: Heart, title: "Direct Aid", description: "Remaining funds go to nonprofits tied to each film's cause." },
];

export default function DonatePage() {
  return (
    <div className="font-body" data-testid="donate-page">
      {/* Hero */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-24" data-testid="donate-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl mx-auto text-center">
            <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.25em] text-[#C86B53] font-semibold mb-4">
              Ready to Support Making the Unseen Seen?
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-none text-[#292524] mb-8">
              Donate
            </motion.h1>
            <motion.p variants={fadeUp} className="text-base sm:text-lg font-medium leading-relaxed text-[#57534E] mb-12">
              Donations directly fund film production, travel, equipment, creator mentorship, and
              community outreach. Every dollar makes a difference.
            </motion.p>
            <motion.div variants={fadeUp}>
              <a
                href="https://www.paypal.com/ncp/payment/3KN4GBXUVPSZL"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="donate-paypal-button"
              >
                <Button className="bg-[#C86B53] hover:bg-[#b55c46] text-white rounded-sm px-12 h-14 text-base font-semibold tracking-wide shadow-lg hover:shadow-xl transition-shadow">
                  <Heart size={18} strokeWidth={2} className="mr-2" />
                  Donate Now via PayPal
                  <ArrowRight size={18} strokeWidth={2} className="ml-2" />
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Where Your Money Goes */}
      <section className="py-24 lg:py-32 bg-[#F2EDE4]" data-testid="donate-impact-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={stagger}
            className="text-center mb-16"
          >
            <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.2em] text-[#C86B53] font-semibold mb-4">
              Your Impact
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-[#292524] mb-4">
              Where Your Money Goes
            </motion.h2>
            <motion.p variants={fadeUp} className="text-base font-medium text-[#57534E] max-w-xl mx-auto">
              We believe in full transparency. Here's how your donations create change.
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {impactAreas.map((area, i) => (
              <motion.div
                key={area.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white/60 p-8 rounded-sm border border-[rgba(41,37,36,0.06)]"
                data-testid={`impact-area-${i}`}
              >
                <div className="w-12 h-12 rounded-full bg-[#C86B53]/10 flex items-center justify-center mb-5">
                  <area.icon size={20} strokeWidth={2} className="text-[#C86B53]" />
                </div>
                <h3 className="font-heading text-xl font-bold text-[#292524] mb-2">
                  {area.title}
                </h3>
                <p className="text-sm font-medium text-[#57534E] leading-relaxed">
                  {area.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-24 lg:py-32" data-testid="donate-trust-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mx-auto text-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={stagger}
            >
              <motion.div variants={fadeUp} className="w-16 h-16 rounded-full bg-[#8A9A86]/15 flex items-center justify-center mx-auto mb-6">
                <Heart size={24} strokeWidth={2} className="text-[#8A9A86]" />
              </motion.div>
              <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#292524] mb-6">
                A Nonprofit You Can Trust
              </motion.h2>
              <motion.p variants={fadeUp} className="text-base font-medium leading-relaxed text-[#57534E] mb-6">
                Visible Foundation is a registered nonprofit organization. We crowdfund each project
                independently, giving donors a clear connection to the stories they support. After
                production, remaining funds are directed toward the causes and nonprofit organizations
                tied to each film.
              </motion.p>
              <motion.p variants={fadeUp} className="text-sm font-medium text-[#57534E]">
                Questions? Reach us at{" "}
                <a href="mailto:thevisible.foundation@gmail.com" className="text-[#C86B53] hover:underline font-semibold">
                  thevisible.foundation@gmail.com
                </a>
              </motion.p>
              <motion.div variants={fadeUp} className="mt-10">
                <a
                  href="https://www.paypal.com/ncp/payment/3KN4GBXUVPSZL"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="donate-paypal-button-bottom"
                >
                  <Button className="bg-[#C86B53] hover:bg-[#b55c46] text-white rounded-sm px-10 h-12 text-sm font-semibold tracking-wide">
                    Donate Now
                    <ArrowRight size={16} strokeWidth={2} className="ml-1" />
                  </Button>
                </a>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
